import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore }    from '../store/cartStore.js';
import { useSessionStore } from '../store/sessionStore.js';
import { useTabsStore }    from '../store/tabsStore.js';
import { getProducts, getCategories } from '../api/catalog.api.js';
import CartPanel    from '../components/layout/CartPanel.jsx';
import SyncBadge    from '../components/common/SyncBadge.jsx';
import NumpadModal  from '../components/NumpadModal.jsx';
import { IcoCheck, IcoPrint, IcoTabs, IcoClipboard, IcoStar, IcoNumpad, IcoPackage } from '../components/common/Icons.jsx';
import styles from './CatalogScreen.module.css';

export default function CatalogScreen() {
  const navigate       = useNavigate();
  const terminal       = useSessionStore((s) => s.terminal);
  const session        = useSessionStore((s) => s.session);
  const addItem        = useCartStore((s) => s.addItem);
  const cartItems      = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const tabCount       = useTabsStore((s) => s.tabs.length);

  const [products,       setProducts]       = useState([]);
  const [search,         setSearch]         = useState('');
  const [loading,        setLoading]        = useState(true);
  const [loadErr,        setLoadErr]        = useState(null);
  const [categories,     setCategories]     = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [scanFlash,      setScanFlash]      = useState(null);
  const [numpadOpen,     setNumpadOpen]     = useState(false);
  const [hasMore,        setHasMore]        = useState(false);
  const [qtyPickerFor,   setQtyPickerFor]   = useState(null);
  const [qtyPickerVal,   setQtyPickerVal]   = useState('');
  const [pinned,         setPinned]         = useState(() => {
    try { return JSON.parse(localStorage.getItem('pos-pinned-products') ?? '[]'); }
    catch { return []; }
  });
  const searchRef = useRef(null);

  const togglePin = (e, product) => {
    e.stopPropagation();
    setPinned((prev) => {
      const exists = prev.find((p) => p._id === product._id);
      const next   = exists
        ? prev.filter((p) => p._id !== product._id)
        : [...prev, { _id: product._id, name: product.name, price: product.price, images: product.images }];
      localStorage.setItem('pos-pinned-products', JSON.stringify(next));
      return next;
    });
  };

  const pinnedFull = pinned
    .map((p) => products.find((pr) => pr._id === p._id) ?? p)
    .filter(Boolean);

  const openQtyPicker = (e, productId, currentQty) => {
    e.stopPropagation();
    setQtyPickerFor(productId);
    setQtyPickerVal(String(currentQty));
  };
  const commitQtyPicker = (productId) => {
    const n = parseInt(qtyPickerVal, 10);
    if (!isNaN(n) && n >= 0) updateQuantity(productId, n);
    setQtyPickerFor(null);
  };

  const cartQty = (productId) =>
    cartItems.find((i) => i.product._id === productId)?.quantity ?? 0;

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    // En tablet el foco automático despliega el teclado en pantalla y tapa
    // el catálogo. Solo enfocamos donde hay puntero fino (mouse/trackpad),
    // que es también donde el escáner de códigos necesita el foco listo.
    // En táctil quedan F2, escribir para buscar, y tocar el campo.
    if (window.matchMedia('(pointer: fine)').matches) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, []);

  // Keyboard shortcuts: F2 → focus search, Escape → clear search
  useEffect(() => {
    const onKey = (e) => {
      // F2: focus search
      if (e.key === 'F2') {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      // Escape: clear search or close numpad
      if (e.key === 'Escape') {
        if (numpadOpen) { setNumpadOpen(false); return; }
        if (search) { setSearch(''); searchRef.current?.focus(); }
        return;
      }
      // Ignore if already in an input
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // Any printable char → focus search and type.
      // Insertamos la tecla a mano: enfocar no reenvía el evento actual, así
      // que sin esto se perdía el primer carácter de cada código escaneado.
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
        setSearch((s) => s + e.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [search, numpadOpen]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const q = search.trim();
      const result = await getProducts({
        ...(q              && { search: q }),
        ...(activeCategory && { category: activeCategory }),
        limit: 200,
      });
      const data = result.data ?? [];
      setProducts(data);
      setHasMore(result.total > data.length);
    } catch (err) {
      setLoadErr(err.response?.data?.error ?? err.message ?? 'Error al cargar productos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [search, activeCategory]);

  useEffect(() => {
    const t = setTimeout(loadProducts, search ? 150 : 0);
    return () => clearTimeout(t);
  }, [loadProducts, search]);

  // Auto-add when scanner sends Enter and there's exactly 1 result
  const handleSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (products.length === 1 && search.trim()) {
      addItem(products[0]);
      setScanFlash(products[0].name);
      setSearch('');
      setTimeout(() => setScanFlash(null), 1500);
      // Re-focus so scanner can scan next item immediately
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  };

  return (
    <div className={styles.root}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <img src="/geek-commerce-dark.svg" alt="Geek Commerce" className={styles.logo} />
          <div className={styles.terminalInfo}>
            <span className={styles.terminalName}>{terminal?.name ?? 'Terminal'}</span>
            <SyncBadge />
          </div>
          {session?.sales_count > 0 && (
            <div className={styles.sessionStats}>
              <span className={styles.sessionSalesCount}>{session.sales_count} venta{session.sales_count !== 1 ? 's' : ''}</span>
              <span className={styles.sessionTotal}>
                ${Number(session.sales_total ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          )}
        </div>
        <div className={styles.headerCenter}>
          <div className={styles.searchRow}>
            <input
              ref={searchRef}
              className={`input ${styles.searchInput}`}
              type="search"
              placeholder="Buscar producto o escanear código…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <button
              className={styles.numpadBtn}
              onClick={() => setNumpadOpen(true)}
              title="Teclado numérico"
            >
              <IcoNumpad size={18} />
            </button>
          </div>
          {scanFlash && (
            <div className={styles.scanFlash}>
              <IcoCheck size={13} /> {scanFlash}
            </div>
          )}
        </div>
        {numpadOpen && (
          <NumpadModal
            onSearch={(code) => setSearch(code)}
            onClose={() => setNumpadOpen(false)}
          />
        )}
        <div className={styles.headerRight}>
          <button
            className="btn btn--ghost"
            title="Devolución"
            onClick={() => navigate('/refund')}
          >
            Devolución
          </button>
          <button
            className="btn btn--ghost"
            style={{ fontSize: 18, padding: '0 12px', minHeight: 36 }}
            title="Configurar impresora"
            onClick={() => navigate('/printer-setup')}
          >
            <IcoPrint size={18} />
          </button>
          <button
            className={`btn btn--ghost ${styles.tabsBtn}`}
            title="Cuentas abiertas"
            onClick={() => navigate('/tabs')}
          >
            <IcoTabs size={15} /> Cuentas{tabCount > 0 && <span className={styles.tabsBadge}>{tabCount}</span>}
          </button>
          <button
            className="btn btn--ghost"
            style={{ fontSize: 18, padding: '0 12px', minHeight: 36 }}
            title="Historial de sesiones"
            onClick={() => navigate('/sessions-history')}
          >
            <IcoClipboard size={18} />
          </button>
          <button
            className="btn btn--ghost"
            onClick={() => navigate('/close-session')}
          >
            Cerrar caja
          </button>
        </div>
      </header>

      {/* ── Category tabs ───────────────────────────────────────────────── */}
      {categories.length > 0 && (
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeCategory === null ? styles.tabActive : ''}`}
            onClick={() => setActiveCategory(null)}
          >
            Todos
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`${styles.tab} ${activeCategory === cat ? styles.tabActive : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* ── Main ───────────────────────────────────────────────────────── */}
      <div className={styles.main}>
        {/* Catálogo */}
        <section className={styles.catalog}>
          {loading ? (
            <div className={styles.loadingGrid}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className={styles.skeletonCard} />
              ))}
            </div>
          ) : loadErr ? (
            <div className={styles.empty}>
              <p>Error: {loadErr}</p>
              <button className="btn btn--ghost" style={{ marginTop: 12 }} onClick={loadProducts}>
                Reintentar
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className={styles.empty}>
              <p>
                {search
                  ? `Sin resultados para "${search}"`
                  : activeCategory
                    ? `Sin productos en "${activeCategory}"`
                    : 'Sin productos disponibles'}
              </p>
            </div>
          ) : (
            <div className={styles.grid}>
              {/* Fila de favoritos */}
              {!search && pinnedFull.length > 0 && (
                <div className={styles.pinnedStrip}>
                  <span className={styles.pinnedLabel}><IcoStar size={13} filled /> Favoritos</span>
                  {pinnedFull.map((product) => (
                    <button
                      key={product._id}
                      className={styles.pinnedChip}
                      onClick={() => addItem(product)}
                    >
                      {product.name}
                      <span className={styles.pinnedPrice}>
                        ${Number(product.price ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {products.map((product) => {
                const qty        = cartQty(product._id);
                const outOfStock = product.stock != null && product.stock <= 0;
                const overStock  = product.stock != null && qty > product.stock;
                const isPinned   = pinned.some((p) => p._id === product._id);
                return (
                  <div key={product._id} className={`${styles.productCard} ${outOfStock ? styles.productCardOut : ''}`}>
                    {qty > 0 && (
                      <div className={`${styles.qtyBadge} ${overStock ? styles.qtyBadgeOver : ''}`}>
                        <button
                          className={styles.qtyBadgeMinus}
                          onClick={(e) => { e.stopPropagation(); updateQuantity(product._id, qty - 1); }}
                        >−</button>
                        {qtyPickerFor === product._id ? (
                          <input
                            className={styles.qtyBadgeInput}
                            type="number"
                            min="0"
                            value={qtyPickerVal}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setQtyPickerVal(e.target.value)}
                            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') commitQtyPicker(product._id); if (e.key === 'Escape') setQtyPickerFor(null); }}
                            onBlur={() => commitQtyPicker(product._id)}
                          />
                        ) : (
                          <span
                            className={styles.qtyBadgeNum}
                            onClick={(e) => openQtyPicker(e, product._id, qty)}
                            title="Toca para cambiar cantidad"
                          >{qty}</span>
                        )}
                      </div>
                    )}
                    {/* Pin button */}
                    <button
                      className={`${styles.pinBtn} ${isPinned ? styles.pinBtnOn : ''}`}
                      onClick={(e) => togglePin(e, product)}
                      title={isPinned ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                    >
                      <IcoStar size={14} filled={isPinned} />
                    </button>
                    <button
                      className={styles.productCardInner}
                      onClick={() => addItem(product)}
                    >
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.name} className={styles.productImg} />
                      ) : (
                        <div className={styles.productImgPlaceholder}>
                          <IcoPackage size={32} />
                        </div>
                      )}
                      <div className={styles.productInfo}>
                        <p className={styles.productName}>{product.name}</p>
                        {product.sku && <p className={styles.productSku}>{product.sku}</p>}
                        <p className={styles.productPrice}>
                          ${product.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </p>
                        {product.stock != null && (
                          <p className={`${styles.productStock} ${outOfStock ? styles.stockOut : product.stock <= 5 ? styles.stockLow : ''}`}>
                            {outOfStock ? 'Sin stock' : `${product.stock} disp.`}
                          </p>
                        )}
                      </div>
                    </button>
                  </div>
                );
              })}
              {hasMore && (
                <div className={`${styles.productCard} ${styles.loadMoreCard}`}>
                  <p className={styles.loadMoreHint}>Usa el buscador para encontrar más productos (mostrando 200).</p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Panel de carrito */}
        <CartPanel />
      </div>
    </div>
  );
}
